import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { X, Trash2, Plus, Sparkles, ListPlus, Pencil } from "lucide-react";
import { CLIENT_CATEGORIES } from "@/pages/admin/Clients";

const overlayStyle = { position: "fixed", inset: 0, background: "rgba(10,10,10,0.7)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 210 };
const modalStyle = { background: "#fff", borderRadius: 12, padding: 20, width: "100%", maxWidth: 560, maxHeight: "88vh", overflowY: "auto" };
const KIND_LABELS = { nacional: "Nacional", provincial_mendoza: "Mendoza", empresa: "Empresa", sectorial: "Por rubro", cliente: "Por cliente" };
const KIND_BADGE = { nacional: "blue", provincial_mendoza: "purple", empresa: "gray", sectorial: "amber", cliente: "teal" };
const EMPTY_FORM = { date: "", name: "", kind: "empresa", applies_to_categories: [], applies_to_client_ids: [], applies_to_site_ids: [] };

export function ScheduleHolidaysPanel({ onClose, canEdit, year, onChangeYear }) {
  const [list, setList] = useState([]);
  const [clients, setClients] = useState([]);
  const [sitesByClient, setSitesByClient] = useState({});
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null);

  const load = () => api.get("/schedule/holidays", { params: { year } }).then(({ data }) => setList(data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);
  useEffect(() => { if (canEdit) api.get("/clients").then(({ data }) => setClients(data || [])).catch(() => setClients([])); }, [canEdit]);

  useEffect(() => {
    if (!form || form.kind !== "cliente" || form.applies_to_client_ids.length === 0) return;
    Promise.all(form.applies_to_client_ids.map((cid) => api.get(`/clients/${cid}/sites`).then(({ data }) => data || []).catch(() => [])))
      .then((results) => {
        const map = {};
        form.applies_to_client_ids.forEach((cid, i) => { map[cid] = results[i]; });
        setSitesByClient(map);
      });
    // eslint-disable-next-line
  }, [form?.kind, form?.applies_to_client_ids?.join(",")]);

  const clientName = (cid) => clients.find((c) => c.id === cid)?.name || cid;

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/schedule/holidays/generate?year=${year}`);
      toast.success(`${data.created} feriado(s) agregado(s)`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const loadSectorCatalog = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/schedule/holidays/load-sector-catalog?year=${year}`);
      toast.success(`${data.created} día(s) de rubro agregado(s)`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const submitAdd = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (form.editingId) {
        await api.patch(`/schedule/holidays/${form.editingId}`, {
          date: form.date, name: form.name, kind: form.kind,
          applies_to_categories: form.applies_to_categories, applies_to_client_ids: form.applies_to_client_ids, applies_to_site_ids: form.applies_to_site_ids,
        });
        toast.success("Día actualizado");
      } else {
        await api.post("/schedule/holidays", form);
        toast.success("Día agregado");
      }
      setForm(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const startEdit = (h) => setForm({
    editingId: h.id, date: h.date, name: h.name, kind: h.kind,
    applies_to_categories: h.applies_to_categories || [], applies_to_client_ids: h.applies_to_client_ids || [], applies_to_site_ids: h.applies_to_site_ids || [],
  });

  const remove = async (h) => {
    if (!window.confirm(`¿Eliminar "${h.name}" (${h.date})?`)) return;
    try { await api.delete(`/schedule/holidays/${h.id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleCategory = (code) => {
    const cur = form.applies_to_categories;
    setForm({ ...form, applies_to_categories: cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code] });
  };
  const toggleClient = (cid) => {
    const cur = form.applies_to_client_ids;
    setForm({ ...form, applies_to_client_ids: cur.includes(cid) ? cur.filter((c) => c !== cid) : [...cur, cid], applies_to_site_ids: [] });
  };
  const toggleSite = (sid) => {
    const cur = form.applies_to_site_ids;
    setForm({ ...form, applies_to_site_ids: cur.includes(sid) ? cur.filter((s) => s !== sid) : [...cur, sid] });
  };

  const scopeLabel = (h) => {
    if (h.kind === "sectorial") return (h.applies_to_categories || []).map((c) => CLIENT_CATEGORIES.find((x) => x.value === c)?.label || c).join(", ") || "—";
    if (h.kind === "cliente") return (h.applies_to_client_ids || []).map(clientName).join(", ") || "—";
    return null;
  };

  const allSites = Object.values(sitesByClient).flat();

  return (
    <div style={overlayStyle} onClick={onClose} data-testid="holidays-panel-overlay">
      <div style={modalStyle} onClick={(e) => e.stopPropagation()} data-testid="holidays-panel">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 900, color: "#0A0A0A" }}>Feriados y no laborables</h3>
          <button onClick={onClose} style={{ border: "none", background: "transparent", cursor: "pointer" }} data-testid="holidays-close"><X size={18} /></button>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
          <input type="number" value={year} onChange={(e) => onChangeYear(Number(e.target.value))} style={{ width: 90 }} data-testid="holidays-year" />
          {canEdit && (
            <>
              <button onClick={generate} disabled={busy} className="btn-ghost" style={{ padding: "8px 12px", fontSize: 11 }} data-testid="holidays-generate">
                <Sparkles size={13} style={{ marginRight: 6 }} />Generar feriados AR + Mendoza
              </button>
              <button onClick={loadSectorCatalog} disabled={busy} className="btn-ghost" style={{ padding: "8px 12px", fontSize: 11 }} data-testid="holidays-load-sector-catalog">
                <ListPlus size={13} style={{ marginRight: 6 }} />Cargar días de rubro
              </button>
            </>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }} data-testid="holidays-list">
          {list.length === 0 && <div style={{ fontSize: 12, color: "var(--invict-text-muted)" }}>Sin feriados cargados para {year}.</div>}
          {list.map((h) => (
            <div key={h.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "6px 0", borderBottom: "1px solid var(--invict-bone-3)" }} data-testid={`holiday-row-${h.id}`}>
              <div>
                <div>
                  <span style={{ fontSize: 12, fontWeight: 800 }}>{h.date}</span>
                  <span style={{ fontSize: 12, marginLeft: 8 }}>{h.name}</span>
                  <span className={`badge ${KIND_BADGE[h.kind] || "gray"}`} style={{ marginLeft: 8, fontSize: 9 }} data-testid={`holiday-kind-${h.id}`}>{KIND_LABELS[h.kind] || h.kind}</span>
                </div>
                {scopeLabel(h) && <div style={{ fontSize: 10, color: "var(--invict-text-muted)", marginTop: 2 }} data-testid={`holiday-scope-${h.id}`}>Aplica a: {scopeLabel(h)}</div>}
              </div>
              {canEdit && (
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={() => startEdit(h)} style={{ border: "none", background: "transparent", color: "var(--invict-text-muted)", cursor: "pointer" }} data-testid={`holiday-edit-${h.id}`}><Pencil size={14} /></button>
                  <button onClick={() => remove(h)} style={{ border: "none", background: "transparent", color: "var(--invict-red)", cursor: "pointer" }} data-testid={`holiday-delete-${h.id}`}><Trash2 size={14} /></button>
                </div>
              )}
            </div>
          ))}
        </div>

        {canEdit && (form ? (
          <form onSubmit={submitAdd} style={{ borderTop: "1px solid var(--invict-bone-3)", paddingTop: 10 }} data-testid="holiday-add-form">
            <div className="field"><label>Fecha</label><input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="holiday-add-date" /></div>
            <div className="field"><label>Nombre</label><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="holiday-add-name" /></div>
            <div className="field"><label>Tipo</label>
              <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value, applies_to_categories: [], applies_to_client_ids: [], applies_to_site_ids: [] })} data-testid="holiday-add-kind">
                <option value="empresa">No laborable de la empresa (todos)</option>
                <option value="provincial_mendoza">Provincial (Mendoza) (todos)</option>
                <option value="nacional">Nacional (todos)</option>
                <option value="sectorial">Por rubro (solo ciertas categorías de cliente)</option>
                <option value="cliente">Por cliente / sede específica</option>
              </select>
            </div>

            {form.kind === "sectorial" && (
              <div className="field" data-testid="holiday-categories-field">
                <label>Rubros a los que aplica</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {CLIENT_CATEGORIES.map((c) => (
                    <button key={c.value} type="button" onClick={() => toggleCategory(c.value)} data-testid={`holiday-category-${c.value}`}
                      style={{ padding: "4px 9px", fontSize: 10, fontWeight: 700, borderRadius: 4, border: "1px solid var(--invict-bone-3)", cursor: "pointer", background: form.applies_to_categories.includes(c.value) ? "#0A0A0A" : "#fff", color: form.applies_to_categories.includes(c.value) ? "#FFFBF4" : "#0A0A0A" }}>
                      {c.label}
                    </button>
                  ))}
                </div>
                {form.applies_to_categories.length === 0 && <p style={{ fontSize: 10, color: "var(--invict-red)", margin: "4px 0 0" }}>Elegí al menos un rubro.</p>}
              </div>
            )}

            {form.kind === "cliente" && (
              <>
                <div className="field" data-testid="holiday-clients-field">
                  <label>Clientes a los que aplica</label>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 140, overflowY: "auto", border: "1px solid var(--invict-bone-3)", borderRadius: 6, padding: 6 }}>
                    {clients.map((c) => (
                      <label key={c.id} style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 6 }} data-testid={`holiday-client-${c.id}`}>
                        <input type="checkbox" checked={form.applies_to_client_ids.includes(c.id)} onChange={() => toggleClient(c.id)} /> {c.name}
                      </label>
                    ))}
                  </div>
                  {form.applies_to_client_ids.length === 0 && <p style={{ fontSize: 10, color: "var(--invict-red)", margin: "4px 0 0" }}>Elegí al menos un cliente.</p>}
                </div>
                {allSites.length > 0 && (
                  <div className="field" data-testid="holiday-sites-field">
                    <label>Sedes puntuales (opcional — vacío = todo el objetivo)</label>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 120, overflowY: "auto", border: "1px solid var(--invict-bone-3)", borderRadius: 6, padding: 6 }}>
                      {allSites.map((s) => (
                        <label key={s.id} style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 6 }} data-testid={`holiday-site-${s.id}`}>
                          <input type="checkbox" checked={form.applies_to_site_ids.includes(s.id)} onChange={() => toggleSite(s.id)} /> {s.name}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <button type="submit" disabled={busy || (form.kind === "sectorial" && form.applies_to_categories.length === 0) || (form.kind === "cliente" && form.applies_to_client_ids.length === 0)} className="btn-primary" style={{ flex: 1, padding: 9 }} data-testid="holiday-add-submit">{form.editingId ? "Guardar cambios" : "Agregar"}</button>
              <button type="button" onClick={() => setForm(null)} style={{ border: "1px solid var(--invict-bone-3)", background: "transparent", borderRadius: 4, padding: "0 14px" }}>Cancelar</button>
            </div>
          </form>
        ) : (
          <button onClick={() => setForm({ ...EMPTY_FORM, date: `${year}-01-01` })} className="btn-ghost" style={{ width: "100%", padding: 9 }} data-testid="holiday-add-open">
            <Plus size={13} style={{ marginRight: 6 }} />Agregar día manual
          </button>
        ))}
      </div>
    </div>
  );
}
